import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  fetchCategories,
  fetchDepartments,
  fetchMe,
  fetchMonthLocks,
  fetchObjectives,
  fetchPeople,
  fetchRagScores,
  fetchReportingPeriods,
  fetchSwaSession,
  type MeResponse,
} from "./api";
import type { RoleName } from "./groups";
import {
  normalisePersonName,
  type CurrentUser,
  type MonthLock,
  type Objective,
  type Person,
  type RagScore,
  type ReportingPeriod,
  type TaxonomyItem,
} from "./types";

// =========================================================================
// DataContext
//
// Top-of-tree context that loads all six entities plus the signed-in user's
// identity + role, then exposes them via useData(). Same shape as the old
// app's DataContext so pages port unchanged.
//
// Auth flow:
//   1. fetch /.auth/me - if null, redirect to /.auth/login/aad and bail.
//   2. fetch /api/me + the six entity lists in parallel.
//   3. Resolve matchedPerson from displayName -> People.fullName.
//   4. Compute covers (active people whose CoveredBy === my fullName).
//   5. Build the role-based permission helpers (canEditFor, canEdit).
// =========================================================================

interface DataContextValue {
  people: Person[];
  objectives: Objective[];
  ragScores: RagScore[];
  monthLocks: MonthLock[];
  reportingPeriods: ReportingPeriod[];
  categories: TaxonomyItem[];
  departments: TaxonomyItem[];
  me: CurrentUser;
  refresh: () => Promise<void>;
}

const DataContext = createContext<DataContextValue | null>(null);

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      meResponse: MeResponse;
      displayName: string;
      username: string;
      people: Person[];
      objectives: Objective[];
      ragScores: RagScore[];
      monthLocks: MonthLock[];
      reportingPeriods: ReportingPeriod[];
      categories: TaxonomyItem[];
      departments: TaxonomyItem[];
    };

/**
 * Resolve effective RoleName from the API's lowercase role string.
 *
 * The API returns `null` when a signed-in user isn't in any of the four
 * NYMR-Objectives security groups. In that case, if their displayName
 * matches a row in People we treat them as "User" (self-service) - same
 * safety-net behaviour as the old DataContext. Otherwise fall through to
 * "Viewer" which the AppShell uses to lock them out of every route.
 */
function resolveRole(
  apiRole: MeResponse["role"],
  matched: Person | null,
): RoleName {
  switch (apiRole) {
    case "admin":
      return "Admin";
    case "exec":
      return "Exec";
    case "clt":
      return "CLT";
    case "user":
      return "User";
    default:
      return matched != null ? "User" : "Viewer";
  }
}

/**
 * Permission rules - single source of truth for who can edit/add for whom.
 * Ported verbatim from the old DataContext.
 */
function makePermissions(
  role: RoleName,
  matched: Person | null,
  covers: Person[],
) {
  const coversNames = new Set(covers.map((p) => normalisePersonName(p.fullName)));
  const canEditFor = (personName: string): boolean => {
    switch (role) {
      case "Admin":
      case "Exec":
        return true;
      case "CLT": {
        const n = normalisePersonName(personName);
        if (matched != null && n === normalisePersonName(matched.fullName))
          return true;
        return coversNames.has(n);
      }
      case "User": {
        if (matched == null) return false;
        const n = normalisePersonName(personName);
        if (n === normalisePersonName(matched.fullName)) return true;
        return coversNames.has(n);
      }
      default:
        return false;
    }
  };
  const canEdit = (o: Objective): boolean =>
    o.status !== "Deleted" && canEditFor(o.personName);
  return { canEditFor, canEdit };
}

/**
 * Resolve a displayable name from the API's MeResponse + SWA principal.
 * Prefers the `name` claim from /api/me; falls back to the SWA principal's
 * userDetails (UPN) - which gives the user *something* even if claims are
 * missing.
 */
function resolveDisplayName(
  meResponse: MeResponse,
  fallbackUpn: string,
): { displayName: string; username: string } {
  return {
    displayName: meResponse.displayName ?? fallbackUpn,
    username: meResponse.upn || fallbackUpn,
  };
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  /**
   * Core fetch routine. `showLoader` controls whether the whole app flips
   * to the "Loading team data..." fullscreen state during the fetch.
   *
   *   true  - first mount and the error-state Retry button
   *   false - quiet background refresh from refresh()
   */
  const performLoad = useCallback(async (showLoader: boolean) => {
    if (showLoader) setState({ status: "loading" });
    try {
      // 1. SWA session check.
      const principal = await fetchSwaSession();
      if (!principal) {
        const returnTo = window.location.pathname + window.location.search;
        window.location.replace(
          `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`,
        );
        return;
      }

      // 2. Load identity + all entity lists in parallel.
      const [
        meResponse,
        people,
        objectives,
        ragScores,
        monthLocks,
        reportingPeriods,
        categories,
        departments,
      ] = await Promise.all([
        fetchMe(),
        fetchPeople(),
        fetchObjectives(),
        fetchRagScores(),
        fetchMonthLocks(),
        fetchReportingPeriods(),
        fetchCategories(),
        fetchDepartments(),
      ]);

      const { displayName, username } = resolveDisplayName(
        meResponse,
        principal.userDetails,
      );

      setState({
        status: "ready",
        meResponse,
        displayName,
        username,
        people,
        objectives,
        ragScores,
        monthLocks,
        reportingPeriods,
        categories,
        departments,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (showLoader) {
        setState({ status: "error", message });
      } else {
        // Quiet refresh failed - keep showing existing data, log for devs.
        console.error("Background refresh failed:", message);
      }
    }
  }, []);

  const load = useCallback(() => performLoad(true), [performLoad]);
  const refresh = useCallback(() => performLoad(false), [performLoad]);

  useEffect(() => {
    void load();
  }, [load]);

  const me = useMemo<CurrentUser | null>(() => {
    if (state.status !== "ready") return null;
    const matchedPerson =
      state.people.find(
        (p) =>
          normalisePersonName(p.fullName) === normalisePersonName(state.displayName),
      ) ?? null;
    const role = resolveRole(state.meResponse.role, matchedPerson);
    const covers = matchedPerson
      ? state.people.filter(
          (p) =>
            p.active &&
            p.coveredBy != null &&
            normalisePersonName(p.coveredBy) ===
              normalisePersonName(matchedPerson.fullName),
        )
      : [];
    const { canEditFor, canEdit } = makePermissions(role, matchedPerson, covers);
    return {
      displayName: state.displayName,
      username: state.username,
      groupIds: state.meResponse.groups,
      role,
      matchedPerson,
      covers,
      canEditFor,
      canEdit,
    };
  }, [state]);

  if (state.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-ink-3">Loading team data...</p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper p-6">
        <div className="max-w-lg rounded-md border border-rag-red bg-rag-red-bg p-4 text-sm text-rag-red">
          <p className="mb-2 font-medium">Failed to load data</p>
          <p className="mb-3 font-mono text-xs whitespace-pre-wrap">{state.message}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md bg-rag-red px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-ink-3">Resolving user...</p>
      </div>
    );
  }

  return (
    <DataContext.Provider
      value={{
        people: state.people,
        objectives: state.objectives,
        ragScores: state.ragScores,
        monthLocks: state.monthLocks,
        reportingPeriods: state.reportingPeriods,
        categories: state.categories,
        departments: state.departments,
        me,
        refresh,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData(): DataContextValue {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used inside DataProvider");
  return ctx;
}
